/**
 * This source code is quoted from rc-switch.
 * homepage: https://github.com/react-component/switch
 */
import classnames from "classnames";
// import PropTypes from "prop-types";
import React, {useState, useEffect, useRef, useCallback, useMemo, useImperativeHandle, forwardRef} from "react";
// import omit from 'omit.js';
// import {Icon} from "../../index";
import Icon from '../../wui-icon/src';
import {WebUI, getNid, prefix} from "../../wui-core/src/index"
import {setComponentSize} from "../../wui-core/src/componentStyle"
import {SwitchProps, SwitchEvent} from './iSwitch'
import {WithConfigConsumer, useConfigContext} from "../../wui-provider/src/context";

function noop() {}

const defaultProps = {
    checkedChildren: null,
    unCheckedChildren: null,
    // checked: false,
    // defaultChecked: false,
    size: "sm",
    disabled: false,
    readOnly: false,
    onChangeHandler: noop,
    onChange: noop,
    onClick: noop,
    onKeyDown: noop,
    onMouseUp: noop,
    enterKeyDown: true,
    loading: false,
    colors: '',
    autoFocus: false,
    fieldid: undefined,
};

const Switch = forwardRef<HTMLButtonElement, SwitchProps>((props, ref) => {
    const configContext = useConfigContext({ name: "switch" });
    const mergedProps = { ...defaultProps, ...configContext, ...props };
    const { checked: propsChecked, checkedChildren = null, unCheckedChildren = null, onChangeHandler, onChange, onClick, onKeyDown, onMouseUp, size = 'sm', className, clsPrefix, disabled = false, readOnly = false, loading = false, colors, enterKeyDown = true, browser, browserClassName, browserStyle, browserText, bordered, defaultValue, defaultChecked, ...others } = mergedProps;
    // 初始化checked状态
    const [checked, setChecked] = useState(() => 'checked' in props ? !!propsChecked : !!(defaultValue ?? defaultChecked));

    const nodeRef = useRef<HTMLButtonElement>(null);

    // 替换getDerivedStateFromProps
    useEffect(() => {
        if ('checked' in props) {
            setChecked(!!propsChecked);
        }
    }, [propsChecked]);

    const setCheckedHandler = useCallback((checkedValue: boolean, e: SwitchEvent) => {
        if (disabled || loading || browser || readOnly) {
            return;
        }
        if (!('checked' in props)) {
            setChecked(checkedValue);
        }
        onChangeHandler?.(checkedValue);
        onChange?.(checkedValue, e);
    }, [disabled, loading, browser, readOnly, onChangeHandler, onChange]);

    // 点击switch改变状态
    const clickHandler = useCallback((e: SwitchEvent) => {
        if (disabled || loading || browser || readOnly) {
            return;
        }
        // 屏蔽button原生支持的enter/space触发的点击事件 isTrusted不阻止模拟点击事件 #QDJCJS-9229
        if (e.type === 'click') {
            const {x, y, isTrusted} = e.nativeEvent as MouseEvent;
            if (x === 0 && y === 0 && !enterKeyDown && isTrusted) {
                return;
            }
        }
        const newChecked = !checked;
        setCheckedHandler(newChecked, e);
        onClick?.(newChecked, e); // 支持onClick事件
    }, [disabled, loading, browser, readOnly, enterKeyDown, onClick, checked, setCheckedHandler]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.keyCode === 37) { // Left
            setCheckedHandler(false, e);
        } else if (e.keyCode === 39) { // Right
            setCheckedHandler(true, e);
        } else if (e.keyCode === 32 || e.keyCode === 13) { // Space, Enter
            // 在测试环境中，第二个参数可能是enterKeyDown值，否则使用props中的值或默认值true
            // const shouldEnterKeyDown = enterKeyDown !== undefined ? enterKeyDown : true;
            if (enterKeyDown) {
                e.preventDefault(); // 阻止Space, Enter默认触发点击事件
                clickHandler(e); // 调用clickHandler而不是直接调用onClick
            }
        }
        onKeyDown?.(e);
    }, [setCheckedHandler, clickHandler, onKeyDown, enterKeyDown]);

    // Handle auto focus when click switch in Chrome
    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if (nodeRef.current) {
            nodeRef.current.blur();
        }
        onMouseUp?.(e);
    }, [onMouseUp]);

    // 使用useImperativeHandle暴露focus和blur方法，同时保持DOM元素的完整兼容性
    useImperativeHandle(ref, () => {
        const node = nodeRef.current;
        if (!node) return null as any;
        // 直接返回DOM节点，这样可以保留所有原生方法和属性
        return node;
    }, []);

    const browserContent = useMemo(() => {
        if (!browser || !browserText) return null;
        const wrapperCls = classnames(`${prefix}-component-browser`, {
            [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
        })
        const componentCls = classnames(`${clsPrefix}-browser`, {
            [`${browserClassName}`]: browserClassName
        })
        return (
            <div className={wrapperCls}>
                <div className={componentCls} style={browserStyle} fieldid={props.fieldid ? props.fieldid : undefined}>
                    {browserText}
                </div>
            </div>
        );
    }, [browser, browserText, bordered, browserClassName, browserStyle, clsPrefix]);

    // 提前返回browser内容
    if (browserContent) {
        return browserContent;
    }

    const isDisabled = disabled || loading || browser;
    const componentSize = setComponentSize(size);
    const sizeClass = componentSize === ('default' as any) ? 'sm' : size === 'small' ? '' : componentSize;

    const classes = {
        "is-checked": checked,
        [`${clsPrefix}-${sizeClass}`]: componentSize,
        [`${clsPrefix}-${colors}`]: colors,
        [`${clsPrefix}-disabled`]: isDisabled,
        [`${clsPrefix}-browser`]: browser,
        [`${clsPrefix}-readonly`]: readOnly,
        [`${clsPrefix}-loading`]: loading,
        [`${clsPrefix}-span`]: true // 类名没用到，暂时保留
    };

    const result: Partial<Pick<SwitchProps, 'id' | 'fieldid'>> = {};
    if (props.id) {
        result.id = `${props.id}_loading_switch`;
    }
    if (props.fieldid) {
        result.fieldid = `${props.fieldid}_loading_switch`;
    }

    let adapterNid = getNid(props);

    return (
        <button
            {...others}
            ref={nodeRef}
            type="button"
            onClick={clickHandler}
            onKeyDown={handleKeyDown}
            onMouseUp={handleMouseUp}
            className={classnames(clsPrefix, classes, className)}
            tabIndex={disabled || browser || readOnly ? -1 : 0}
            {...adapterNid}
        >
            {loading ? <Icon {...result} type="uf-loadingstate"/> : ''}
            <span className={`${clsPrefix}-inner`}>
                {checked ? checkedChildren : unCheckedChildren}
            </span>
        </button>
    );
});

// Switch.propTypes = propTypes;
export default WithConfigConsumer({name: "switch"})(WebUI({name: "switch", defaultProps})(Switch));
